import { z } from 'zod';
import { expiryStatusOf } from '../readiness/evaluate.js';
import { credentialDetailsSchema, verificationOutcomeSchema } from './evidence-details.js';
import { fileIdSchema } from '../files/stored-file.js';
/**
 * A credential the agency holds for a person: a licence, a certification, a cleared check.
 *
 * The shape is dictated by `CredentialEvidence` in `readiness/evaluate.ts` — that interface
 * is the spec, and anything here that does not feed it is decoration.
 */
/**
 * The live record is keyed on (personId, requirementKey), and a renewal SUPERSEDES rather
 * than appends.
 *
 * This is the single most consequential decision in the file. `fromCredentials` returns
 * `indeterminate` with `conflicting_evidence` the moment two live credentials for one key
 * disagree on expiry. An append-per-instance design passes every Phase 2 test — there is
 * only ever one credential per key while the first packet is being built — and then turns
 * every Phase 4 renewal into "Needs review" across the entire roster, at the exact moment
 * the system is under real use.
 *
 * Superseded records are RETAINED with the flag set, because "what did we hold in March"
 * is a question a surveyor asks, and excluded from evidence, because it is not what we
 * hold now.
 */
export function liveCredentialId(personId, requirementKey) {
    return `cred:${personId}:${requirementKey}`;
}
export const credentialItemSchema = z.object({
    id: z.string().min(1),
    personId: z.string().min(1),
    docType: z.literal('credential-item').default('credential-item'),
    /** Which requirement this closes. The join key for evidence. */
    requirementKey: z.string().min(1),
    /** Licence or certificate number, when the document carries one. Never derived. */
    identifier: z.string().nullable().default(null),
    issuedAt: z.string().datetime().nullable().default(null),
    expiresAt: z.string().datetime().nullable().default(null),
    details: credentialDetailsSchema.optional(),
    /**
     * A credential HR has since invalidated. `fromCredentials` reads a person with only
     * rejected credentials as `rejected` rather than `not_started`, which is the difference
     * between "we are waiting for this" and "we looked and it was not acceptable".
     */
    rejected: z.boolean().default(false),
    /** Set when a newer credential replaced this one. Retained, but never evidence. */
    supersededAt: z.string().datetime().nullable().default(null),
    supersededBy: z.string().min(1).nullable().default(null),
    /** Where it came from, so a credential can be traced back to what the person sent. */
    sourceInstanceId: z.string().min(1).nullable().default(null),
    /**
     * An HR-typed evidence link, or — LEGACY — the SharePoint item a file was routed to (#61).
     * Nothing writes a SharePoint URL here since ADR-009.
     */
    sourceFileUrl: z.string().url().nullable().default(null),
    /**
     * The committed FileRecord the credential's document is (#183), stamped by the `store-file`
     * handler or at mint, whichever runs second. Null until the file is committed, and for
     * credentials recorded without a document.
     */
    sourceFileId: fileIdSchema.nullable().default(null),
    recordedAt: z.string().datetime(),
    recordedBy: z.string().min(1),
});
/**
 * An external check that was performed — a registry lookup, a licence verification.
 *
 * Distinct from a credential: a credential is a thing the person HAS, a verification is
 * something the agency DID. Q27's unanswered re-check interval is about this record, not
 * about a credential.
 */
export const verificationEventSchema = z.object({
    id: z.string().min(1),
    personId: z.string().min(1),
    docType: z.literal('verification-event').default('verification-event'),
    requirementKey: z.string().min(1),
    verifiedAt: z.string().datetime().nullable(),
    verifiedBy: z.string().min(1),
    /** What was checked against. A registry name, never a URL with a person in it. */
    source: z.string().default(''),
    outcome: verificationOutcomeSchema.default('clear'),
    note: z.string().max(1000).default(''),
    reference: z.string().max(200).optional(),
    evidenceUrl: z.string().url().nullable().optional(),
    recordedAt: z.string().datetime().optional(),
    recordedBy: z.string().min(1).optional(),
    dateSource: z.enum(['performed', 'source-submission', 'unknown']).optional(),
});
/** Live means: not rejected, not superseded. Both exclusions matter and neither is implied. */
export function isLiveCredential(credential) {
    return !credential.rejected && credential.supersededAt === null;
}
export function countExpiry(credentials, expiryFor, now) {
    let expired = 0;
    let expiringSoon = 0;
    let counted = 0;
    let unassessable = 0;
    for (const credential of credentials) {
        if (credential.expiresAt === null)
            continue;
        const rule = expiryFor(credential.requirementKey);
        if (rule === null) {
            unassessable += 1;
            continue;
        }
        counted += 1;
        const status = expiryStatusOf(credential.expiresAt, rule, now);
        if (status === 'expired')
            expired += 1;
        else if (status === 'expiring_soon')
            expiringSoon += 1;
    }
    return { expired, expiringSoon, counted, unassessable };
}
/**
 * Which of the two reported buckets this credential falls in, or null for neither.
 *
 * The same classifier `countExpiry` folds over, exposed so a caller listing rows and a caller
 * counting them cannot drift apart: every row a caller shows is, by construction, one of the
 * records the counts came from.
 */
export function reportableExpiry(credential, expiryFor, now) {
    if (credential.expiresAt === null)
        return null;
    const rule = expiryFor(credential.requirementKey);
    if (rule === null)
        return null;
    const status = expiryStatusOf(credential.expiresAt, rule, now);
    return status === 'expired' || status === 'expiring_soon' ? status : null;
}
//# sourceMappingURL=model.js.map